import React, { useEffect, useMemo, useRef } from 'react';
import { Button, Segmented } from 'antd';
import { UndoOutlined, RedoOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { ReplayStop } from '../graphql/types';
import { REPLAY_PRIMARY } from '../theme';

interface ManualAdjustPanelProps {
    order: ReplayStop[];
    startPoint?: ReplayStop | null;
    mode: 'single' | 'range';
    onModeChange: (m: 'single' | 'range') => void;
    anchorKey?: string | null;
    rangeKeys?: string[];
    focus?: { key: string; seq: number } | null;
    canUndo?: boolean;
    canRedo?: boolean;
    onUndo?: () => void;
    onRedo?: () => void;
    onReset?: () => void;
    onExit: () => void;
    onViewResult: () => void;
}

/** 手动调整模式右侧「调整送货顺序」面板 */
const ManualAdjustPanel: React.FC<ManualAdjustPanelProps> = ({
    order,
    startPoint,
    mode,
    onModeChange,
    anchorKey,
    rangeKeys = [],
    focus,
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    onReset,
    onExit,
    onViewResult,
}) => {
    const { t } = useTranslation();
    // 高亮的 stop 集合：区间模式 rangeKeys 已覆盖整簇；单点模式把锚点所在的连续同坐标簇整段纳入
    const highlightSet = useMemo(() => {
        const set = new Set<string>();
        if (mode !== 'single') {
            rangeKeys.forEach((k) => set.add(k));
            return set;
        }
        if (!anchorKey) return set;
        const idx = order.findIndex((s) => s.stop === anchorKey);
        if (idx < 0) return set;
        const { latitude: lat, longitude: lng } = order[idx];
        let lo = idx;
        let hi = idx;
        while (lo - 1 >= 0 && order[lo - 1].latitude === lat && order[lo - 1].longitude === lng) lo--;
        while (hi + 1 < order.length && order[hi + 1].latitude === lat && order[hi + 1].longitude === lng) hi++;
        for (let i = lo; i <= hi; i++) set.add(order[i].stop);
        return set;
    }, [mode, anchorKey, rangeKeys, order]);
    // 每行地址的 DOM 引用；点地图标点后把对应行滚进可视区（#4）
    const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    useEffect(() => {
        if (!focus) return;
        rowRefs.current.get(focus.key)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [focus?.seq]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div className="w-[360px] h-full flex flex-col rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
            {/* 头部 */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
                <div className="text-[15px] font-semibold text-gray-900">{t('delivery.routeReplay.manual.title')}</div>
                <Button size="small" className="!text-xs" onClick={onExit}>{t('delivery.routeReplay.manual.exit')}</Button>
            </div>

            {/* 操作区 */}
            <div className="p-3 space-y-3">
                {/* 单点 / 区间 tab：track 圆到 12px(对齐说明框)，选中块用 antd 默认内缩圆角，不错位 */}
                <Segmented
                    block
                    className="!rounded-xl [&_.ant-segmented-thumb]:!rounded-lg [&_.ant-segmented-item-selected]:!rounded-lg [&_.ant-segmented-item-label]:font-semibold [&_.ant-segmented-item-label]:py-1"
                    value={mode}
                    onChange={(v) => onModeChange(v as 'single' | 'range')}
                    options={[
                        { label: t('delivery.routeReplay.manual.single'), value: 'single' },
                        { label: t('delivery.routeReplay.manual.range'), value: 'range' },
                    ]}
                />

                {/* 上一步/下一步一组、恢复算法顺序一组，两组 space-between（统一文案按钮） */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            icon={<UndoOutlined />}
                            className="!text-xs"
                            style={{ background: '#f3f4f6', borderColor: '#e5e7eb', color: '#4b5563' }}
                            disabled={!canUndo}
                            onClick={onUndo}
                        >
                            {t('delivery.routeReplay.manual.undo')}
                        </Button>
                        <Button
                            icon={<RedoOutlined />}
                            className="!text-xs"
                            style={{ background: '#f3f4f6', borderColor: '#e5e7eb', color: '#4b5563' }}
                            disabled={!canRedo}
                            onClick={onRedo}
                        >
                            {t('delivery.routeReplay.manual.redo')}
                        </Button>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        className="!text-xs"
                        style={{ background: '#f3f4f6', borderColor: '#e5e7eb', color: '#4b5563' }}
                        onClick={onReset}
                    >
                        {t('delivery.routeReplay.manual.resetToAlgorithm')}
                    </Button>
                </div>

                {/* 查看本次结果：加高加粗 */}
                <Button
                    block
                    type="primary"
                    onClick={onViewResult}
                    style={{ height: 44, fontWeight: 600, background: '#16a34a', borderColor: '#16a34a' }}
                >
                    {t('delivery.routeReplay.manual.viewResult')}
                </Button>

                {/* 单点调整说明 */}
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-2.5 text-xs leading-relaxed text-gray-600">
                    <span className="inline-block rounded border border-amber-300 bg-white text-amber-600 px-1.5 py-0.5 mr-1 text-[11px]">{t('delivery.routeReplay.manual.single')}</span>
                    <Trans
                        i18nKey="delivery.routeReplay.manual.singleHelp"
                        components={[
                            <span className="text-orange-500" />,
                            <span className="text-orange-500 font-medium" />,
                            <kbd className="px-1 rounded border border-gray-300 bg-white text-gray-500 text-[11px]" />,
                        ]}
                    />
                </div>

                {/* 区间调整说明 */}
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-2.5 text-xs leading-relaxed text-gray-600">
                    <span className="inline-block rounded border border-gray-300 bg-white text-gray-600 px-1.5 py-0.5 mr-1 text-[11px]">{t('delivery.routeReplay.manual.range')}</span>
                    <Trans
                        i18nKey="delivery.routeReplay.manual.rangeHelp"
                        components={[
                            <span className="text-orange-500" />,
                            <span className="text-orange-500" />,
                            <span className="text-orange-500" />,
                        ]}
                    />
                </div>
            </div>

            {/* 停靠点顺序列表 */}
            <div className="flex-1 overflow-y-auto px-3 pb-3">
                <div className="rounded-xl bg-[#eef2f7] px-3 py-2 mb-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <HomeOutlined className="text-gray-500" /> {t('delivery.routeReplay.manual.startFixed')}
                    </div>
                    {startPoint?.streetAddress && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate">{startPoint.streetAddress}</div>
                    )}
                </div>
                {order.map((s, i) => {
                    const isHi = highlightSet.has(s.stop);
                    return (
                        <div
                            key={s.stop}
                            ref={(el) => {
                                if (el) rowRefs.current.set(s.stop, el);
                                else rowRefs.current.delete(s.stop);
                            }}
                            className={`flex items-center gap-3 py-2 px-2 rounded-lg ${isHi ? 'bg-orange-50' : ''}`}
                        >
                            <span className="w-6 text-right font-semibold" style={isHi ? { color: '#f97316' } : { color: REPLAY_PRIMARY }}>{i + 1}</span>
                            <span className={`truncate ${isHi ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>{s.streetAddress ?? '—'}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ManualAdjustPanel;
